/**
 * Demo Mode Wizard Modal - Sprint 16
 * Guided demo workflow for sales demonstrations
 * 
 * Features:
 * - Scenario-based demo scripts
 * - Step-by-step wizard with narration
 * - Deep links to relevant pages
 * - Progress tracking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play, Pause, ChevronRight, ChevronLeft, X, ExternalLink, 
  Loader2, CheckCircle, Circle, Info, RefreshCw, Sparkles,
  Rocket, MapPin, Target, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step Progress Indicator
const StepIndicator = ({ currentStep, totalSteps, onStepClick }) => {
  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2 px-1">
      {Array.from({ length: totalSteps }, (_, idx) => (
        <button
          key={idx}
          onClick={() => onStepClick(idx)}
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
            idx < currentStep
              ? 'bg-green-500 text-white'
              : idx === currentStep
              ? 'bg-blue-600 text-white ring-2 ring-blue-300'
              : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
          }`}
        >
          {idx < currentStep ? <CheckCircle className="w-4 h-4" /> : idx + 1}
        </button>
      ))}
    </div>
  );
};

// Tips Panel
const TipsPanel = ({ tips }) => {
  if (!tips || tips.length === 0) return null;
  
  return (
    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
      <div className="flex items-center gap-2 text-amber-700 font-medium text-sm mb-2">
        <Info className="w-4 h-4" />
        Demo Tips
      </div>
      <ul className="text-sm text-amber-800 space-y-1">
        {tips.map((tip, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <span className="text-amber-500">*</span>
            {tip}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Scenario Selector
const ScenarioSelector = ({ scenarios, selectedScenario, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <h3 className="font-medium text-gray-800 mb-3">Select Demo Scenario</h3>
      {scenarios.map((scenario) => (
        <button
          key={scenario.id}
          onClick={() => onSelect(scenario.id)}
          className={`w-full text-left p-4 rounded-lg border transition-all ${
            selectedScenario === scenario.id
              ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-800">{scenario.name}</span>
            <span className="text-xs text-gray-500">{scenario.duration_minutes} min</span>
          </div>
          <p className="text-sm text-gray-600 mt-1">{scenario.description}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
            <MapPin className="w-3 h-3" />
            {scenario.step_count} steps
          </div>
        </button>
      ))}
    </div>
  );
};

// Main Demo Mode Modal
const DemoModeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [script, setScript] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingScript, setLoadingScript] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [demoIds, setDemoIds] = useState(null);
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);
  
  // Load scenarios
  useEffect(() => {
    if (!isOpen) return;
    
    const loadScenarios = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/demo/scenarios`, {
          headers: getAuthHeaders()
        });
        
        if (res.ok) {
          const data = await res.json();
          setScenarios(data.scenarios || []);
        }
      } catch (err) {
        console.error('Failed to load scenarios:', err);
        toast.error('Failed to load demo scenarios');
      } finally {
        setLoading(false);
      }
    };
    
    loadScenarios();
  }, [isOpen, getAuthHeaders]);
  
  // Load script when scenario selected
  const handleSelectScenario = async (scenarioId) => {
    setSelectedScenario(scenarioId);
    setLoadingScript(true);
    
    try {
      const res = await fetch(`${API_URL}/api/demo/script?scenario=${scenarioId}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setScript(data);
        setCurrentStepIndex(0);
      }
    } catch (err) {
      console.error('Failed to load script:', err);
      toast.error('Failed to load demo script');
    } finally {
      setLoadingScript(false);
    }
  };
  
  // Seed demo data
  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API_URL}/api/demo/seed`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setDemoIds(data.demo_ids);
        toast.success('Demo data loaded successfully');
        
        // Mark step complete and advance
        handleMarkComplete();
      } else {
        toast.error('Failed to seed demo data');
      }
    } catch (err) {
      console.error('Failed to seed data:', err);
      toast.error('Failed to load demo data');
    } finally {
      setSeeding(false);
    }
  };
  
  // Navigate to page
  const handleOpenPage = (pageUrl) => {
    if (!pageUrl) return;
    
    // Replace placeholders with demo IDs
    let url = pageUrl;
    if (demoIds) {
      url = url.replace('{demo_demand_id}', demoIds.demand_id || '');
      url = url.replace('{demo_project_id}', demoIds.project_id || '');
      url = url.replace('{demo_portfolio_id}', demoIds.portfolio_id || '');
    }
    
    navigate(url);
    handleMarkComplete();
  };
  
  // Mark step complete and advance
  const handleMarkComplete = async () => {
    if (!script || !selectedScenario) return;
    
    const currentStep = script.steps[currentStepIndex];
    
    try {
      await fetch(`${API_URL}/api/demo/progress?scenario=${selectedScenario}&step_id=${currentStep.step_id}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch (err) {
      // Ignore progress tracking errors
    }
    
    // Advance to next step
    if (currentStepIndex < script.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };
  
  // Navigation
  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };
  
  const handleNextStep = () => {
    if (currentStepIndex < (script?.steps.length || 0) - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };
  
  const handleGoToStep = (index) => {
    setCurrentStepIndex(index);
  };
  
  // Reset demo
  const handleReset = () => {
    setSelectedScenario(null);
    setScript(null);
    setCurrentStepIndex(0);
    setDemoIds(null);
  };
  
  if (!isOpen) return null;
  
  const currentStep = script?.steps?.[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === (script?.steps?.length || 0) - 1;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="demo-mode-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Rocket className="w-6 h-6" />
            <div>
              <h2 className="font-semibold text-lg">Demo Mode</h2>
              <p className="text-sm text-white/80">
                {script ? script.name : 'Sales Demo Wizard'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {script && (
              <button
                onClick={handleReset}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Reset Demo"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!script ? (
            <ScenarioSelector
              scenarios={scenarios}
              selectedScenario={selectedScenario}
              onSelect={handleSelectScenario}
              loading={loading || loadingScript}
            />
          ) : (
            <>
              {/* Progress Indicator */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Step {currentStepIndex + 1} of {script.steps.length}</span>
                  <span>{Math.round(((currentStepIndex + 1) / script.steps.length) * 100)}% Complete</span>
                </div>
                <StepIndicator
                  currentStep={currentStepIndex}
                  totalSteps={script.steps.length}
                  onStepClick={handleGoToStep}
                />
              </div>
              
              {/* Current Step */}
              {currentStep && (
                <div className="space-y-4">
                  {/* Step Title */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Target className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">
                        {currentStep.title}
                      </h3>
                      <p className="text-sm text-gray-600">{currentStep.description}</p>
                    </div>
                  </div>
                  
                  {/* Narration */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 font-medium text-sm mb-2">
                      <FileText className="w-4 h-4" />
                      Narration Script
                    </div>
                    <p className="text-sm text-blue-900 italic">
                      "{currentStep.narration}"
                    </p>
                  </div>
                  
                  {/* Tips */}
                  <TipsPanel tips={currentStep.tips} />
                  
                  {/* Action Button */}
                  <div className="pt-4">
                    {currentStep.action_type === 'api_call' && currentStep.action_url?.includes('seed') ? (
                      <button
                        onClick={handleSeedData}
                        disabled={seeding || demoIds}
                        className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {seeding ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : demoIds ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Sparkles className="w-5 h-5" />
                        )}
                        {demoIds ? 'Demo Data Loaded' : currentStep.cta_label || 'Load Demo Data'}
                      </button>
                    ) : currentStep.page_url ? (
                      <button
                        onClick={() => handleOpenPage(currentStep.page_url)}
                        className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 flex items-center justify-center gap-2"
                      >
                        <ExternalLink className="w-5 h-5" />
                        {currentStep.cta_label || 'Open Page'}
                      </button>
                    ) : (
                      <button
                        onClick={handleMarkComplete}
                        className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Mark as Complete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer Navigation */}
        {script && (
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
            <button
              onClick={handlePrevStep}
              disabled={isFirstStep}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            
            <div className="text-sm text-gray-500">
              {script.duration_minutes} min estimated
            </div>
            
            <button
              onClick={handleNextStep}
              disabled={isLastStep}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoModeModal;
